import React from "react";
import { MonitorIcon, SmartphoneIcon, TabletIcon } from "lucide-react";
import { MenuButtonItem } from "@repo/ui";

/** Device viewports the preview can be framed at (VERA-268). */
export type DeviceKey = "mobile" | "tablet" | "desktop";

export interface DeviceFrame {
  label: string;
  /** Natural viewport the app renders at before scaling. */
  viewportWidth: number;
  viewportHeight: number;
  /** Rendered (scaled) width of the framed device in the preview pane. */
  width: number;
  /**
   * What "100%" renders at FOR THIS DEVICE.
   *
   * Per-device, not one container constant, because the constant that existed was tuned
   * on the mobile frame and the three devices are not the same shape. At the old shared
   * 1.35, `width * baseZoom` gave mobile 351px (fits), tablet 567px (tight), and desktop
   * 1026px — into a pane that is roughly 640px at the default split, so desktop arrived
   * clipped and the user had to wheel-zoom out before it was usable. Same failure the
   * shared constant was introduced to fix for mobile, one device over.
   *
   * The rule: 100% should be a resting state where the WHOLE device is visible. `zoom`
   * remains the user's own multiplier on top, so the label still reads 100% at rest.
   */
  baseZoom: number;
  /** Leading icon in the device picker. */
  icon: React.ReactNode;
}

export const DEVICE_FRAMES: Record<DeviceKey, DeviceFrame> = {
  // 1.35 -> 351x760. The measured value from VRG-221; whole device visible.
  mobile: {
    label: "Mobile",
    viewportWidth: 390,
    viewportHeight: 844,
    width: 260,
    baseZoom: 1.35,
    icon: React.createElement(SmartphoneIcon, { size: 16, strokeWidth: 1 }),
  },
  // 1.0 -> 420x604. Portrait and tall, so HEIGHT is what binds here, not width.
  tablet: {
    label: "Tablet",
    viewportWidth: 820,
    viewportHeight: 1180,
    width: 420,
    baseZoom: 1,
    icon: React.createElement(TabletIcon, { size: 16, strokeWidth: 1 }),
  },
  // 0.8 -> 608x380. Landscape, so WIDTH binds: the pane is ~640px at the default
  // split and 608 clears it with room for the bezel.
  desktop: {
    label: "Desktop",
    viewportWidth: 1280,
    viewportHeight: 800,
    width: 760,
    baseZoom: 0.8,
    icon: React.createElement(MonitorIcon, { size: 16, strokeWidth: 1 }),
  },
};

export const DEVICE_OPTIONS: MenuButtonItem[] = (
  Object.keys(DEVICE_FRAMES) as DeviceKey[]
).map((key) => ({
  label: DEVICE_FRAMES[key].label,
  value: key,
  icon: DEVICE_FRAMES[key].icon,
}));

/**
 * Customer-app flows the Certificate-tab preview can show. The `value` is the
 * `?page=` token the app's StudioPreviewPage switches on (VERA-268 / VERA-275).
 */
export const APP_FLOW_PAGES: MenuButtonItem[] = [
  { label: "Certificates", value: "certificates" },
  { label: "Certificate", value: "certificate" },
  // The PUBLIC composition of the same page: the brand header instead of the owner
  // nav bar, and none of the owner actions. Its own entry rather than a toggle because
  // the two are different screens, and the brand-header switch only governs this one.
  { label: "Certificate (public view)", value: "certificate-public" },
  { label: "Claim ownership", value: "claim-ownership" },
  { label: "Purchase product", value: "purchase-product" },
];
